
import React, { useState } from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Send, User } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useAddTicketMessage } from '@/hooks/useSupportTickets';
import { formatDate } from '@/utils/formatters';
import { SupportTicket } from '@/types';

interface TicketConversationProps {
  ticket: SupportTicket;
  author: any;
}

const TicketConversation: React.FC<TicketConversationProps> = ({ ticket, author }) => {
  const { profile } = useAuth();
  const addMessageMutation = useAddTicketMessage();
  const [replyMessage, setReplyMessage] = useState('');

  const handleSubmitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyMessage.trim() || !profile?.id || !ticket.id) return;
    
    try {
      await addMessageMutation.mutateAsync({
        ticketId: ticket.id,
        userId: profile.id,
        message: replyMessage.trim()
      });
      setReplyMessage('');
    } catch (error) {
      console.error('Error submitting reply:', error);
    }
  };

  // Get initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conversation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {ticket.messages?.map((message: any) => {
          const isAdmin = message.userRole === 'admin';
          const messageAuthor = isAdmin ? profile : author;
          
          return (
            <div 
              key={message.id}
              className={`flex ${isAdmin ? 'justify-end' : ''}`}
            >
              <div className={`flex gap-3 max-w-[80%] ${isAdmin ? 'flex-row-reverse' : ''}`}>
                <Avatar className="h-10 w-10 mt-1">
                  <AvatarImage src={messageAuthor?.avatar_url} />
                  <AvatarFallback className={isAdmin ? 'bg-brand-500' : 'bg-emerald-600'}>
                    {messageAuthor?.name ? getInitials(messageAuthor.name) : <User className="h-5 w-5" />}
                  </AvatarFallback>
                </Avatar>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">
                      {messageAuthor?.name || message.userName || 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-500">
                      {formatDate(message.createdAt)}
                    </span>
                    {isAdmin && (
                      <Badge variant="outline" className="text-xs">Staff</Badge>
                    )}
                  </div>
                  
                  <div className={`p-3 rounded-lg ${
                    isAdmin 
                      ? 'bg-brand-50 text-brand-800 border border-brand-200' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {message.message}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>
      
      <CardFooter>
        <form onSubmit={handleSubmitReply} className="w-full">
          <div className="space-y-4">
            <Textarea
              placeholder="Type your reply here..."
              className="min-h-[100px]"
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
              disabled={addMessageMutation.isPending}
            />
            <div className="flex justify-end">
              <Button 
                type="submit" 
                disabled={!replyMessage.trim() || addMessageMutation.isPending}
                className="bg-brand-500 hover:bg-brand-600"
              >
                <Send className="mr-2 h-4 w-4" />
                {addMessageMutation.isPending ? 'Sending...' : 'Send Reply'}
              </Button>
            </div>
          </div>
        </form>
      </CardFooter>
    </Card>
  );
};

export default TicketConversation;
